import { axiosInstance } from '../../App'
import {
  DoseFrequencyStartingHourListItemDTO,
  DoseFrequencyStartingHourListParams,
  DoseFrequencyStartingHourSaveDTO,
} from '../../dto/dosefrequencies/DoseFrequenciesDTO'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function toRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null
}

function toStringSafe(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim()
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function parsePositiveNumber(value: unknown): number | null {
  const parsed = parseNumber(value)
  return parsed != null && parsed > 0 ? parsed : null
}

function extractArray(rawData: unknown): unknown[] {
  if (Array.isArray(rawData)) return rawData
  const rawObj = toRecord(rawData)
  if (!rawObj) return []
  if (Array.isArray(rawObj.data)) return rawObj.data
  if (Array.isArray(rawObj.items)) return rawObj.items
  if (Array.isArray(rawObj.content)) return rawObj.content
  return []
}

function extractEffectiveFilters(filterDTO: unknown, cardFilterDTO: unknown): unknown {
  const pick = (value: unknown): unknown | null => {
    const record = toRecord(value)
    if (!record) return null
    const filters = Array.isArray(record.filters) ? record.filters : []
    const filterLists = Array.isArray(record.filterLists) ? record.filterLists : []
    if (filters.length === 0 && filterLists.length === 0) return null
    return value
  }

  return pick(filterDTO) ?? pick(cardFilterDTO) ?? null
}

function normalizeDoseFrequencyStartingHourListItem(item: unknown): DoseFrequencyStartingHourListItemDTO | null {
  const rawObj = toRecord(item)
  if (!rawObj) return null

  const doseFrequencyId = parsePositiveNumber(
    rawObj.doseFrequencyId ?? rawObj.dosefrequencyid ?? rawObj.doseFrequency ?? rawObj.dosefrequency,
  )
  if (doseFrequencyId == null) return null

  const startingHour = parseNumber(rawObj.startingHour ?? rawObj.startinghour)

  return {
    doseFrequencyId,
    doseFrequencyLabel: toStringSafe(rawObj.doseFrequencyLabel ?? rawObj.dosefrequencylabel),
    startingHour: startingHour != null ? Math.trunc(startingHour) : null,
  }
}

export function useDoseFrequenciesService() {
function getDoseFrequencyStartingHours(
  offSet: number,
  pageSize: number,
  params: DoseFrequencyStartingHourListParams = {},
): Promise<DoseFrequencyStartingHourListItemDTO[]> {
  const filters = extractEffectiveFilters(params.filterDTO, params.cardFilterDTO)

  return axiosInstance.post('/api/secured/dose-frequencies/list', {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters,
    },
  }).then((response) => extractArray(response.data)
    .map((item) => normalizeDoseFrequencyStartingHourListItem(item))
    .filter((item): item is DoseFrequencyStartingHourListItemDTO => item !== null))
}

function updateDoseFrequencyStartingHour(
  doseFrequencyId: number,
  payload: DoseFrequencyStartingHourSaveDTO,
): Promise<void> {
  return axiosInstance.put(`/api/secured/dose-frequencies/${doseFrequencyId}/starting-hour`, payload)
    .then(() => undefined)
}

  return {
    getDoseFrequencyStartingHours,
    updateDoseFrequencyStartingHour,
  }
}
